import React, { useState, useEffect, useContext } from "react";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Typography,
  CircularProgress,
  withStyles,
  Card,
  ListItem,
  Select,
  MenuItem,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Edit as EditIcon } from "@material-ui/icons";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { useParams } from "react-router-dom";
import { Instance } from "../../../Config";
import OwnerHeader from "../Components/OwnerHeader";
import GlobalContext from "../../../Context/globalContext";
import { SHOW_SNACKBAR } from "../../../Context/globalActions.types";
import { Skeleton } from "@material-ui/lab";

const useStyles = makeStyles((theme) => ({
  paper: {
    width: "100%",
    marginBottom: theme.spacing(2),
  },
  tableContainer: {
    maxHeight: 600,
  },
}));

const StyledTableCell = withStyles((theme) => ({
  head: {
    backgroundColor: "#3f51b5",
    color: theme.palette.common.white,
    fontWeight: 600,
  },
}))(TableCell);

const StyledTableRow = withStyles((theme) => ({
  root: {
    "&:nth-of-type(even)": {
      backgroundColor: theme.palette.action.hover,
    },
    "&:hover": {
      backgroundColor: "#F3F3F3",
      transform: "scale(1.0019)",
    },
  },
}))(TableRow);

const ProductTranslations = () => {
  const { mobileNo } = useParams();
  const { dispatch } = useContext(GlobalContext);
  const [languages, setLanguages] = useState([]);
  const [selectedLang, setSelectedLang] = useState("");
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [openSuccessDialog, setOpenSuccessDialog] = useState(false);
  const [openErrorDialog, setOpenErrorDialog] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(false);

  // Form for translation
  const [translationForm, setTranslationForm] = useState({
    ITEM_NAME: "",
    ITEM_SNAME: "",
    ITEM_LNAME: "",
    ITEM_DESC: "",
  });

  useEffect(() => {
    fetchLanguages();
    fetchProducts();
  }, []);

  useEffect(() => {
    if (selectedLang && selectedProduct) {
      fetchProductTranslation();
    }
  }, [selectedLang, selectedProduct]);

  const fetchLanguages = async () => {
    setDataLoading(true);
    try {
      const response = await Instance.get(`/languages?mobileNo=${mobileNo}`);
      const langs = Array.isArray(response.data)
        ? response.data
        : response.data?.result || response.data?.languages || [];
      setLanguages(langs);
      const nonEnglish = langs.find((l) => l.lang_code !== "en");
      if (nonEnglish) {
        setSelectedLang(nonEnglish.lang_code);
      }
    } catch (error) {
      console.error("Error fetching languages:", error);
      dispatch({
        type: SHOW_SNACKBAR,
        payload: {
          message: "Error fetching languages",
          type: "error",
        },
      });
    } finally {
      setDataLoading(false);
    }
  };

  const fetchProducts = async () => {
    setDataLoading(true);
    try {
      const response = await Instance.get(
        `/products/getAllItems?mobileNo=${mobileNo}`
      );
      setProducts(response.data.result || []);
    } catch (error) {
      console.error("Error fetching products:", error);
      dispatch({
        type: SHOW_SNACKBAR,
        payload: {
          message: "Error fetching products",
          type: "error",
        },
      });
    } finally {
      setDataLoading(false);
    }
  };

  const fetchProductTranslation = async () => {
    setDataLoading(true);
    try {
      const response = await Instance.get(
        `/translations/product/${selectedProduct.ITEM_CD}?mobileNo=${mobileNo}&lang_code=${selectedLang}`
      );
      const existingTranslation = response.data.find(
        (t) => t.lang_code === selectedLang
      );
      if (existingTranslation) {
        setTranslationForm({
          ITEM_NAME: existingTranslation.ITEM_NAME || "",
          ITEM_SNAME: existingTranslation.ITEM_SNAME || "",
          ITEM_LNAME: existingTranslation.ITEM_LNAME || "",
          ITEM_DESC: existingTranslation.ITEM_DESC || "",
        });
      } else {
        setTranslationForm({
          ITEM_NAME: "",
          ITEM_SNAME: "",
          ITEM_LNAME: "",
          ITEM_DESC: "",
        });
      }
    } catch (error) {
      console.error("Error fetching translation:", error);
      setTranslationForm({
        ITEM_NAME: "",
        ITEM_SNAME: "",
        ITEM_LNAME: "",
        ITEM_DESC: "",
      });
    } finally {
      setDataLoading(false);
    }
  };

  const handleSaveTranslation = async () => {
    setLoading(true);
    try {
      await Instance.post(`/translations/product`, {
        ITEM_CD: selectedProduct.ITEM_CD,
        lang_code: selectedLang,
        ...translationForm,
      });
      setOpenDialog(false);
      setOpenSuccessDialog(true);
      fetchProductTranslation();
    } catch (error) {
      console.error("Error saving translation:", error);
      setErrorMessage(
        error.response?.data?.message || "Error saving translation"
      );
      setOpenErrorDialog(true);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    setOpenDialog(true);
  };

  return (
    <>
      <OwnerHeader>
        <div className="md:flex items-center justify-between px-3 md:px-4 py-3">
          <div>
            <div className="text-indigo-500 font-semibold text-sm md:text-base">
              Translation Management
            </div>
            <div className="text-xl md:text-2xl font-extrabold leading-7 sm:leading-10 truncate mt-1">
              Product Translations
            </div>
          </div>
        </div>

        <div className="py-1 min-h-screen bg-[#F1F5F9] px-2">
          <Card>
            <ListItem className="pb-0">
              <Typography
                variant="h6"
                className="text-indigo-500 uppercase font-bold tracking-wider text-xs"
              >
                Filters
              </Typography>
            </ListItem>
            <ListItem className="pt-0">
              <Typography
                variant="h6"
                className="text-gray-500 uppercase font-bold tracking-wider text-[9px]"
              >
                Select language and product to view and edit translations
              </Typography>
            </ListItem>
          </Card>

          <div className="mx-2 md:flex items-end gap-4 mb-4">
            <FormControl
              variant="outlined"
              className="md:w-64 w-full"
              margin="normal"
            >
              <InputLabel>Language</InputLabel>
              <Select
                value={selectedLang}
                onChange={(e) => setSelectedLang(e.target.value)}
                label="Language"
                className="bg-white"
              >
                {languages
                  .filter((l) => l.lang_code !== "en")
                  .map((lang) => (
                    <MenuItem key={lang.lang_code} value={lang.lang_code}>
                      {lang.lang_name} ({lang.native_name})
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>

            <Autocomplete
              options={products}
              getOptionLabel={(option) =>
                `${option.ITEM_CD} - ${option.ITEM_NAME}`
              }
              value={selectedProduct}
              onChange={(event, newValue) => {
                setSelectedProduct(newValue);
              }}
              className="md:w-64 w-full"
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Search Product"
                  variant="outlined"
                  margin="normal"
                  className="bg-white"
                />
              )}
            />

            <Button
              variant="contained"
              disableElevation
              className="mb-2 bg-[#3333cc] text-white h-[56px]"
              onClick={fetchProductTranslation}
              disabled={!selectedLang || !selectedProduct}
            >
              Apply
            </Button>
          </div>

          <React.Fragment>
            <TableContainer
              style={{
                overflowX: "clip",
              }}
              component={Paper}
            >
              <div className="md:flex justify-between w-full my-3 px-2 pt-3">
                <Typography className="md:ml-2 text-md md:text-lg mb-2 font-medium">
                  {selectedProduct
                    ? `Translating: ${selectedProduct.ITEM_NAME}`
                    : "Product Translations"}
                </Typography>
                {selectedProduct && selectedLang && (
                  <Button
                    variant="contained"
                    color="primary"
                    disableElevation
                    startIcon={<EditIcon />}
                    onClick={handleEdit}
                    className="bg-[#3333cc]"
                  >
                    Edit Translation
                  </Button>
                )}
              </div>

              {selectedProduct && selectedLang && (
                <div className="max-h-screen overflow-x-hidden">
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <StyledTableCell>Field</StyledTableCell>
                        <StyledTableCell>English (Original)</StyledTableCell>
                        <StyledTableCell>
                          {languages.find((l) => l.lang_code === selectedLang)
                            ?.lang_name || selectedLang}{" "}
                          (Translation)
                        </StyledTableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {dataLoading ? (
                        Array.from(new Array(4)).map((_, index) => (
                          <StyledTableRow key={index}>
                            <StyledTableCell>
                              <Skeleton />
                            </StyledTableCell>
                            <StyledTableCell>
                              <Skeleton />
                            </StyledTableCell>
                            <StyledTableCell>
                              <Skeleton />
                            </StyledTableCell>
                          </StyledTableRow>
                        ))
                      ) : (
                        <>
                          <StyledTableRow>
                            <TableCell>
                              <strong>Item Name</strong>
                            </TableCell>
                            <TableCell>{selectedProduct.ITEM_NAME}</TableCell>
                            <TableCell>
                              {translationForm.ITEM_NAME || (
                                <em style={{ color: "#999" }}>
                                  Not translated
                                </em>
                              )}
                            </TableCell>
                          </StyledTableRow>
                          <StyledTableRow>
                            <TableCell>
                              <strong>Short Name</strong>
                            </TableCell>
                            <TableCell>{selectedProduct.ITEM_SNAME}</TableCell>
                            <TableCell>
                              {translationForm.ITEM_SNAME || (
                                <em style={{ color: "#999" }}>
                                  Not translated
                                </em>
                              )}
                            </TableCell>
                          </StyledTableRow>
                          <StyledTableRow>
                            <TableCell>
                              <strong>Long Name</strong>
                            </TableCell>
                            <TableCell>{selectedProduct.ITEM_LNAME}</TableCell>
                            <TableCell>
                              {translationForm.ITEM_LNAME || (
                                <em style={{ color: "#999" }}>
                                  Not translated
                                </em>
                              )}
                            </TableCell>
                          </StyledTableRow>
                          <StyledTableRow>
                            <TableCell>
                              <strong>Description</strong>
                            </TableCell>
                            <TableCell>{selectedProduct.ITEM_DESC}</TableCell>
                            <TableCell>
                              {translationForm.ITEM_DESC || (
                                <em style={{ color: "#999" }}>
                                  Not translated
                                </em>
                              )}
                            </TableCell>
                          </StyledTableRow>
                        </>
                      )}
                    </TableBody>
                  </Table>
                </div>
              )}

              {!selectedProduct && (
                <div className="p-8 text-center">
                  <Typography variant="body2" color="textSecondary">
                    Please select a language and product to view translations
                  </Typography>
                </div>
              )}
            </TableContainer>
          </React.Fragment>

          {/* Edit Translation Dialog */}
          <Dialog
            open={openDialog}
            onClose={() => setOpenDialog(false)}
            maxWidth="md"
            fullWidth
          >
            <DialogTitle>
              Edit Translation - {selectedProduct?.ITEM_NAME}
            </DialogTitle>
            <DialogContent>
              <TextField
                label="Item Name"
                fullWidth
                margin="normal"
                variant="outlined"
                value={translationForm.ITEM_NAME}
                onChange={(e) =>
                  setTranslationForm({
                    ...translationForm,
                    ITEM_NAME: e.target.value,
                  })
                }
                helperText={`Original: ${selectedProduct?.ITEM_NAME}`}
              />

              <TextField
                label="Short Name"
                fullWidth
                margin="normal"
                variant="outlined"
                value={translationForm.ITEM_SNAME}
                onChange={(e) =>
                  setTranslationForm({
                    ...translationForm,
                    ITEM_SNAME: e.target.value,
                  })
                }
                helperText={`Original: ${selectedProduct?.ITEM_SNAME}`}
              />

              <TextField
                label="Long Name"
                fullWidth
                margin="normal"
                variant="outlined"
                value={translationForm.ITEM_LNAME}
                onChange={(e) =>
                  setTranslationForm({
                    ...translationForm,
                    ITEM_LNAME: e.target.value,
                  })
                }
                helperText={`Original: ${selectedProduct?.ITEM_LNAME}`}
              />

              <TextField
                label="Description"
                fullWidth
                margin="normal"
                variant="outlined"
                multiline
                rows={4}
                value={translationForm.ITEM_DESC}
                onChange={(e) =>
                  setTranslationForm({
                    ...translationForm,
                    ITEM_DESC: e.target.value,
                  })
                }
                helperText={`Original: ${selectedProduct?.ITEM_DESC}`}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setOpenDialog(false)} color="default">
                Cancel
              </Button>
              <Button
                onClick={handleSaveTranslation}
                color="primary"
                variant="contained"
                disableElevation
                disabled={loading}
                className="bg-[#3333cc]"
              >
                {loading ? (
                  <>
                    <CircularProgress size={20} className="mr-2" /> Saving...
                  </>
                ) : (
                  "Save Translation"
                )}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Success Dialog */}
          <Dialog
            open={openSuccessDialog}
            onClose={() => setOpenSuccessDialog(false)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>Success</DialogTitle>
            <DialogContent>
              <Typography>Translation saved successfully!</Typography>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => setOpenSuccessDialog(false)}
                color="primary"
                variant="contained"
                disableElevation
                className="bg-[#3333cc]"
              >
                OK
              </Button>
            </DialogActions>
          </Dialog>

          {/* Error Dialog */}
          <Dialog
            open={openErrorDialog}
            onClose={() => setOpenErrorDialog(false)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>Error</DialogTitle>
            <DialogContent>
              <Typography>{errorMessage}</Typography>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => setOpenErrorDialog(false)}
                color="secondary"
                variant="contained"
                disableElevation
              >
                OK
              </Button>
            </DialogActions>
          </Dialog>
        </div>
      </OwnerHeader>
    </>
  );
};

export default ProductTranslations;
